"use client";

import Link from "next/link";
import { Instagram, Facebook } from "lucide-react";
import { usePathname } from "next/navigation";
import { trackGalleryCtaClicked } from "@/lib/posthog-client";

const linkClass =
  "text-sm text-white/45 hover:text-white transition-colors";

const sectionLabelClass =
  "text-[11px] font-semibold uppercase tracking-wider text-white/35";

export function Footer() {
  const pathname = usePathname();
  const isAppPage = pathname.startsWith("/app");

  if (isAppPage) {
    return null;
  }

  return (
    <footer className="bg-[#080A0D] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-x-12 lg:gap-y-10">
          <div className="space-y-5 sm:col-span-2 lg:col-span-5">
            <Link href="/" className="inline-flex items-center gap-2">
              <img src="/logo-rim.png" alt="The Wheel Visualizer" className="h-6 w-auto" />
              <span className="font-bold text-lg text-white">
                <span className="italic">The</span> Wheel Visualizer
              </span>
            </Link>
            <ul className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-white/40">
              <li>No subscriptions</li>
              <li className="text-white/20 max-sm:hidden" aria-hidden>
                ·
              </li>
              <li>Credits never expire</li>
              <li className="text-white/20 max-sm:hidden" aria-hidden>
                ·
              </li>
              <li>Secure payments</li>
            </ul>
          </div>

          <div className="space-y-4 lg:col-span-2">
            <p className={sectionLabelClass}>Site</p>
            <nav className="flex flex-col items-start gap-3" aria-label="Site">
              <Link href="/wheel-guide" className={linkClass}>
                Wheel Guide
              </Link>
              <Link href="/wheel-brands" className={linkClass}>
                Wheel Brands
              </Link>
              <Link
                href="/gallery"
                className={linkClass}
                onClick={() => trackGalleryCtaClicked({ location: "footer" })}
              >
                Gallery
              </Link>
              <Link href="/contact" className={linkClass}>
                Contact
              </Link>
              <Link href="/for-dealers" className={linkClass}>
                For Dealers
              </Link>
            </nav>
          </div>

          <div className="space-y-4 lg:col-span-2">
            <p className={sectionLabelClass}>Popular</p>
            <nav className="flex flex-col items-start gap-3" aria-label="Popular wheel visualizers">
              <Link href="/bmw-wheel-visualizer" className={linkClass}>
                BMW Wheel Visualizer
              </Link>
              <Link href="/mustang-wheel-visualizer" className={linkClass}>
                Mustang Wheel Visualizer
              </Link>
              <Link href="/audi-wheel-visualizer" className={linkClass}>
                Audi Wheel Visualizer
              </Link>
              <Link href="/blog" className={linkClass}>
                Blog
              </Link>
            </nav>
          </div>

          <div className="space-y-4 lg:col-span-3">
            <p className={sectionLabelClass}>Legal</p>
            <nav className="flex flex-col items-start gap-3 mb-6" aria-label="Legal">
              <Link href="/terms" className={linkClass}>
                Terms
              </Link>
              <Link href="/privacy" className={linkClass}>
                Privacy
              </Link>
            </nav>
            <p className={sectionLabelClass}>Follow</p>
            <nav className="flex flex-col items-start gap-3" aria-label="Social">
              <a
                href="https://instagram.com/wheelvisualizer"
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} inline-flex items-center gap-2`}
              >
                <Instagram className="w-4 h-4 shrink-0 opacity-80" />
                <span>@wheelvisualizer</span>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61587326601167"
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} inline-flex items-center gap-2`}
              >
                <Facebook className="w-4 h-4 shrink-0 opacity-80" />
                <span>Facebook</span>
              </a>
            </nav>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/5 text-center md:text-left">
          <p className="text-sm text-white/30">
            © {new Date().getFullYear()} TheWheelVisualizer.com. All rights reserved.
          </p>
          <p className="text-sm text-white/30 mt-2">
            Independent service. Not affiliated with or endorsed by any wheel manufacturer. All brand names and trademarks are the property of their respective owners.
          </p>
        </div>
      </div>
    </footer>
  );
}
